
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Client, ExtendedClientPackage } from "@/lib/supabase";
import { User, Activity, Dumbbell, Heart, Star, Calendar, Ruler, Weight, HeartPulse } from "lucide-react";
import PackageHoursProgress from "./PackageHoursProgress";

interface ClientInfoTabProps {
  client: Client;
  clientPackages?: ExtendedClientPackage[];
}

const ClientInfoTab = ({ client, clientPackages = [] }: ClientInfoTabProps) => {
  return (
    <div className="space-y-6">
      {clientPackages && clientPackages.length > 0 && (
        <PackageHoursProgress clientPackages={clientPackages} />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Statistiche rapide */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="shadow-sm hover:shadow transition-all duration-300 p-3 bg-gradient-to-br from-blue-50 to-blue-100/30 dark:from-blue-900/20 dark:to-blue-800/10">
            <CardContent className="p-3 flex flex-col items-center justify-center">
              <Dumbbell className="h-8 w-8 text-blue-500 mb-2 animate-pulse" />
              <span className="text-sm text-muted-foreground">Frequenza</span>
              <span className="text-xl font-semibold mt-1">{client.training_frequency || "N/D"}</span>
            </CardContent>
          </Card>
          
          <Card className="shadow-sm hover:shadow transition-all duration-300 p-3 bg-gradient-to-br from-green-50 to-green-100/30 dark:from-green-900/20 dark:to-green-800/10">
            <CardContent className="p-3 flex flex-col items-center justify-center">
              <Activity className="h-8 w-8 text-green-500 mb-2 animate-pulse" />
              <span className="text-sm text-muted-foreground">Condizione</span>
              <span className="text-xl font-semibold mt-1">{client.general_condition || "N/D"}</span>
            </CardContent>
          </Card>
          
          <Card className="shadow-sm hover:shadow transition-all duration-300 p-3 bg-gradient-to-br from-purple-50 to-purple-100/30 dark:from-purple-900/20 dark:to-purple-800/10">
            <CardContent className="p-3 flex flex-col items-center justify-center">
              <Ruler className="h-8 w-8 text-purple-500 mb-2 animate-pulse" />
              <span className="text-sm text-muted-foreground">Altezza</span>
              <span className="text-xl font-semibold mt-1">{client.height ? `${client.height} cm` : "N/D"}</span>
            </CardContent>
          </Card>
          
          <Card className="shadow-sm hover:shadow transition-all duration-300 p-3 bg-gradient-to-br from-amber-50 to-amber-100/30 dark:from-amber-900/20 dark:to-amber-800/10">
            <CardContent className="p-3 flex flex-col items-center justify-center">
              <Weight className="h-8 w-8 text-amber-500 mb-2 animate-pulse" />
              <span className="text-sm text-muted-foreground">Peso</span>
              <span className="text-xl font-semibold mt-1">{client.weight ? `${client.weight} kg` : "N/D"}</span>
            </CardContent>
          </Card>
        </div>
        
        {/* Dati personali */}
        <Card className="shadow-sm hover:shadow transition-all duration-300 bg-gradient-to-br from-gray-50 to-primary/5 dark:from-gray-900/20 dark:to-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <User size={18} className="text-primary" />
              Dati Personali
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-white/50 dark:bg-black/10 p-2 rounded-md">
                <span className="text-muted-foreground text-xs">Nome</span>
                <p className="font-medium">{client.first_name}</p>
              </div>
              <div className="bg-white/50 dark:bg-black/10 p-2 rounded-md">
                <span className="text-muted-foreground text-xs">Cognome</span>
                <p className="font-medium">{client.last_name}</p>
              </div>
              <div className="bg-white/50 dark:bg-black/10 p-2 rounded-md">
                <span className="text-muted-foreground text-xs">Età</span>
                <p className="font-medium">{client.age} anni</p>
              </div>
              <div className="bg-white/50 dark:bg-black/10 p-2 rounded-md">
                <span className="text-muted-foreground text-xs">Genere</span>
                <p className="font-medium">{client.gender === 'male' ? 'Uomo' : client.gender === 'female' ? 'Donna' : 'Altro'}</p>
              </div>
            </div>
            
            <div className="bg-white/50 dark:bg-black/10 p-2 rounded-md">
              <span className="text-muted-foreground text-xs">Email</span>
              <p className="font-medium">{client.email || "-"}</p>
            </div>
            
            <div className="bg-white/50 dark:bg-black/10 p-2 rounded-md">
              <span className="text-muted-foreground text-xs">Telefono</span>
              <p className="font-medium">{client.telefono || "-"}</p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Sport e interessi */}
      <Card className="shadow-sm hover:shadow transition-all duration-300">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Star size={18} className="text-yellow-500" />
            Sport e Interessi
          </CardTitle>
          <CardDescription>
            Attività e preferenze sportive del cliente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/40 dark:to-gray-800/40 p-4 rounded-lg">
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                <Calendar size={14} className="text-primary" />
                Frequenza di allenamento
              </h4>
              <p className="bg-white/80 dark:bg-black/20 py-2 px-3 rounded-md text-sm">{client.training_frequency || "Non specificato"}</p>
            </div>
            
            <div className="mb-4">
              <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                <HeartPulse size={14} className="text-red-500" />
                Condizione generale
              </h4>
              <p className="bg-white/80 dark:bg-black/20 py-2 px-3 rounded-md text-sm">{client.general_condition || "Non specificato"}</p>
            </div>
            
            <div>
              <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
                <Star size={14} className="text-yellow-500" />
                Sport praticati
              </h4>
              <div className="flex flex-wrap gap-2">
                {client.sports && client.sports.length > 0 ? (
                  client.sports.map((sport, index) => (
                    <Badge key={index} className="bg-primary/10 hover:bg-primary/20 text-primary py-1 px-2 transition-all duration-300 hover:scale-105">
                      {sport}
                    </Badge>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">Nessuno sport specificato</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientInfoTab;
